import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { coreAuth, ICoreUser } from '../../Lib/coreApi'

type TAuthStatus = 'loading' | 'authenticated' | 'unauthenticated'

interface IAuthContext {
  status: TAuthStatus
  user: ICoreUser | undefined
  refresh: () => Promise<void>
  logout: () => Promise<void>
}

export const AuthContext = React.createContext<IAuthContext>({
  status: 'loading',
  user: undefined,
  refresh: async (): Promise<void> => undefined,
  logout: async (): Promise<void> => undefined,
})

export const useAuth = (): IAuthContext => useContext(AuthContext)

/** Cookie-based Core auth (replaces Auth0). Session is checked via GET /user/me. */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [status, setStatus] = useState<TAuthStatus>('loading')
  const [user, setUser] = useState<ICoreUser | undefined>(undefined)

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const me = await coreAuth.me()
      setUser(me)
      setStatus('authenticated')
    } catch {
      setUser(undefined)
      setStatus('unauthenticated')
    }
  }, [])

  const logout = useCallback(async (): Promise<void> => {
    try {
      await coreAuth.logout()
    } finally {
      setUser(undefined)
      setStatus('unauthenticated')
    }
  }, [])

  useEffect(() => {
    refresh()
  }, [refresh])

  const value = useMemo(() => ({ status, user, refresh, logout }), [status, user, refresh, logout])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
